import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import ListToolbar from 'cms/components/ListToolbar';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const renderStatusChip = (status) => {
    if (status === undefined || status === null) {
        return <Chip label="Unknown" size="small" color="default" />;
    }
    const statusStr = String(status).toLowerCase();
    if (statusStr === 'true' || statusStr === 'active' || status === true) {
        return <Chip label="Active" size="small" color="success" />;
    }
    return <Chip label="Inactive" size="small" color="error" />;
};

function CVList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCVId, setSelectedCVId] = useState(null);

    const { data, isLoading, isFetching, refetch } = useQuery(
        ['admin_cv_list'],
        async () => {
            const service = new HttpService();
            return await service.get('/job/cv/admin/list/', null);
        }
    );

    const filteredCVs = useMemo(() => {
        if (!data?.results) return [];
        if (!searchTerm) return data.results;
        const term = searchTerm.toLowerCase();
        return data.results.filter(cv => 
            formatText(cv.title).toLowerCase().includes(term) ||
            formatText(cv.name).toLowerCase().includes(term) ||
            formatText(cv.last_name).toLowerCase().includes(term) ||
            formatText(cv.email).toLowerCase().includes(term)
        );
    }, [data, searchTerm]);

    const handleDeleteRequest = (cvId) => {
        setSelectedCVId(cvId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteDone = () => {
        queryClient.invalidateQueries(['admin_cv_list']);
        setDeleteDialogOpen(false);
        setSelectedCVId(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            <ListToolbar
                title="CV Management"
                subtitle={`${filteredCVs.length} CV${filteredCVs.length === 1 ? '' : 's'}`}
                search={{
                    value: searchTerm,
                    onChange: setSearchTerm,
                    placeholder: 'Search by title, name, email…'
                }}
                onRefresh={() => refetch()}
                refreshing={isFetching}
            />

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCVs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        {searchTerm ? 'No CVs found matching your search.' : 'No CVs found.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredCVs.map((cv) => (
                                <TableRow key={cv.id} hover>
                                    <TableCell>{cv.id}</TableCell>
                                    <TableCell>{formatText(cv.title)}</TableCell>
                                    <TableCell>{formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || '—')}</TableCell>
                                    <TableCell>{formatText(cv.email)}</TableCell>
                                    <TableCell>
                                        {cv.user?.profile?.first_name && cv.user?.profile?.last_name
                                            ? `${cv.user.profile.first_name} ${cv.user.profile.last_name}`
                                            : cv.user?.email || '—'}
                                    </TableCell>
                                    <TableCell>{renderStatusChip(cv.status)}</TableCell>
                                    <TableCell>{cv.created_at ? Utils.formatDate(cv.created_at) : '—'}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Tooltip title="View details">
                                                <IconButton color="primary"
                                                    size="small"
                                                    component={Link}
                                                    to={`/cms/cv/${cv.id}`}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteRequest(cv.id)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <DeleteDialog
                url={`/job/cv/${selectedCVId}`}
                isOpen={deleteDialogOpen}
                key={`delete-cv-${deleteDialogOpen}-${selectedCVId}`}
                setOpen={setDeleteDialogOpen}
                notifyDone={handleDeleteDone}
            />
        </Stack>
    );
}

export default CVList;

